
function parseReturnToPath(returnTo) {
  if (typeof returnTo !== "string" || !returnTo.startsWith("/")) return null;
  try {
    const u = new URL(returnTo, "http://local.invalid");
    return { pathname: u.pathname, search: u.search || "" };
  } catch {
    return { pathname: returnTo, search: "" };
  }
}

/**
 * Persist orders dashboard filters in URL query params (read on mount, write on apply).
 */

export const ORDERS_FILTER_STATE_PERSIST_TRACE_TAG = "[ORDERS_FILTER_STATE_PERSIST_TRACE]";

const LEGACY_DEEP_LINK_KEYS = new Set(["orderType"]);

function strip(v) {
  return v == null ? "" : String(v).trim();
}

function setDateRangeParams(sp, prefix, range) {
  const start = strip(range?.start);
  const end = strip(range?.end);
  if (start) sp.set(`${prefix}Start`, start);
  if (end) sp.set(`${prefix}End`, end);
}

function readDateRangeParams(sp, startKey, endKey) {
  const start = strip(sp.get(startKey));
  const end = strip(sp.get(endKey));
  if (!start && !end) return { start: "", end: "" };
  return { start, end };
}

function boolParam(v) {
  return v === true || v === "true" || v === "1" || v === 1;
}

/** @param {URLSearchParams} searchParams */
export function isLegacyOrdersDeepLinkSearch(searchParams) {
  if (!(searchParams instanceof URLSearchParams)) return false;
  if (!searchParams.get("orderType")) return false;
  return Boolean(
    searchParams.get("orderId") ||
      searchParams.get("mainContact") ||
      searchParams.get("hotelName")
  );
}

/**
 * @param {Record<string, unknown>} filters
 * @param {number} tabIndex
 * @returns {URLSearchParams}
 */
export function buildOrdersFilterSearchParams(filters = {}, tabIndex = 0) {
  const sp = new URLSearchParams();
  const f = filters && typeof filters === "object" ? filters : {};

  if (tabIndex === 2) {
    const status = strip(f.orderStatus);
    if (status && status !== "all" && status !== "active") sp.set("orderStatus", status);
    if (boolParam(f.notPaid)) sp.set("notPaid", "1");
    if (boolParam(f.cxlDropRisk)) sp.set("cxlRisk", "1");
    if (f.freeCancellationDays != null && f.freeCancellationDays !== "") {
      sp.set("freeCancellationDays", String(f.freeCancellationDays));
    }
  } else if (tabIndex === 3) {
    const status = strip(f.status);
    if (status) sp.set("status", status);
    if (strip(f.paid)) sp.set("paid", strip(f.paid));
    if (boolParam(f.pending)) sp.set("pending", "1");
    if (boolParam(f.cancelled)) sp.set("cancelled", "1");
    if (boolParam(f.flight_booked)) sp.set("flightBooked", "1");
    if (boolParam(f.hotel_booked)) sp.set("hotelBooked", "1");
    if (strip(f.min_price)) sp.set("minPrice", strip(f.min_price));
    if (strip(f.max_price)) sp.set("maxPrice", strip(f.max_price));
  } else {
    const mergeRole = strip(f.merge_role) || "all";
    if (mergeRole && mergeRole !== "all") sp.set("mergeRole", mergeRole);
    const paidStatus = strip(f.paidStatus);
    if (paidStatus) sp.set("paymentStatus", paidStatus);
    const refundable = strip(f.refundableuntil);
    if (refundable) sp.set("refundableuntil", refundable);
  }

  const agentEmail = strip(f.agent_email);
  if (agentEmail) sp.set("agentEmail", agentEmail);
  const agentFilterId = strip(f.agent_filter_id);
  if (agentFilterId) sp.set("agentFilterId", agentFilterId);
  const hotelName = strip(f.hotel_name);
  if (hotelName) sp.set("hotelName", hotelName);
  const mainContact = strip(f.main_contact);
  if (mainContact) sp.set("mainContact", mainContact);
  const orderId = strip(f.order_id);
  if (orderId) sp.set("orderId", orderId);
  if (boolParam(f.showCancelled)) sp.set("showCancelled", "1");

  setDateRangeParams(sp, "orderDate", f.order_dates);
  setDateRangeParams(sp, "stayDate", f.staying_dates);

  return sp;
}

/**
 * @param {URLSearchParams} searchParams
 * @param {number} tabIndex
 * @returns {Record<string, unknown>}
 */
export function parseOrdersFiltersFromSearchParams(searchParams, tabIndex = 0) {
  if (!(searchParams instanceof URLSearchParams)) return {};
  const out = {};

  if (tabIndex === 2) {
    const orderStatus = strip(searchParams.get("orderStatus"));
    if (orderStatus) out.orderStatus = orderStatus;
    if (boolParam(searchParams.get("notPaid"))) out.notPaid = true;
    if (boolParam(searchParams.get("cxlRisk"))) out.cxlDropRisk = true;
    const fcd = searchParams.get("freeCancellationDays");
    if (fcd != null && strip(fcd) !== "") {
      const n = Number(fcd);
      out.freeCancellationDays = Number.isFinite(n) ? n : fcd;
    }
  } else if (tabIndex === 3) {
    const status = strip(searchParams.get("status"));
    if (status) out.status = status;
    const paid = strip(searchParams.get("paid"));
    if (paid) out.paid = paid;
    if (boolParam(searchParams.get("pending"))) out.pending = true;
    if (boolParam(searchParams.get("cancelled"))) out.cancelled = true;
    if (boolParam(searchParams.get("flightBooked"))) out.flight_booked = true;
    if (boolParam(searchParams.get("hotelBooked"))) out.hotel_booked = true;
    const minP = strip(searchParams.get("minPrice"));
    if (minP) out.min_price = minP;
    const maxP = strip(searchParams.get("maxPrice"));
    if (maxP) out.max_price = maxP;
  } else {
    const mergeRole = strip(searchParams.get("mergeRole"));
    if (mergeRole) out.merge_role = mergeRole;
    const paymentStatus = strip(searchParams.get("paymentStatus"));
    if (paymentStatus) out.paidStatus = paymentStatus;
    const refundable = strip(searchParams.get("refundableuntil"));
    if (refundable) out.refundableuntil = refundable;
  }

  const agentEmail = strip(searchParams.get("agentEmail"));
  if (agentEmail) out.agent_email = agentEmail;
  const agentFilterId = strip(searchParams.get("agentFilterId"));
  if (agentFilterId) out.agent_filter_id = agentFilterId;
  const hotelName = strip(searchParams.get("hotelName"));
  if (hotelName) out.hotel_name = hotelName;
  const mainContact = strip(searchParams.get("mainContact"));
  if (mainContact) out.main_contact = mainContact;
  const orderId = strip(searchParams.get("orderId"));
  if (orderId) out.order_id = orderId;
  if (boolParam(searchParams.get("showCancelled"))) out.showCancelled = true;

  const orderDates = readDateRangeParams(searchParams, "orderDateStart", "orderDateEnd");
  if (orderDates.start || orderDates.end) out.order_dates = orderDates;
  const stayDates = readDateRangeParams(searchParams, "stayDateStart", "stayDateEnd");
  if (stayDates.start || stayDates.end) out.staying_dates = stayDates;

  return out;
}

export function ordersFilterSearchToString(searchParams) {
  const s = searchParams instanceof URLSearchParams ? searchParams.toString() : "";
  return s ? `?${s}` : "";
}

export function areOrdersFilterSearchParamsEqual(a, b) {
  const sa = a instanceof URLSearchParams ? a.toString() : String(a || "");
  const sb = b instanceof URLSearchParams ? b.toString() : String(b || "");
  return sa === sb;
}

/**
 * @param {import('react-router-dom').Location} listLocation
 * @param {object} orderRow
 */
export function buildOrdersDetailNavigationState(orderRow, listLocation) {
  return {
    order: orderRow,
    ordersListReturn: {
      pathname: listLocation?.pathname || "",
      search: listLocation?.search || "",
    },
  };
}

/**
 * @param {import('react-router-dom').Location} location
 * @param {string} fallbackPathname
 */
export function resolveOrdersListBackTarget(location, fallbackPathname) {
  const ret = location?.state?.ordersListReturn;
  if (ret?.pathname) {
    return {
      pathname: ret.pathname,
      search: ret.search || "",
    };
  }
  return { pathname: fallbackPathname, search: "" };
}

export function emitOrdersFilterStatePersistTrace(source, filters, locationSearch = "") {
  if (!import.meta.env.DEV || typeof console === "undefined" || !console.info) return;
  try {
    console.info(ORDERS_FILTER_STATE_PERSIST_TRACE_TAG, {
      source,
      filters,
      locationSearch: locationSearch || "",
    });
  } catch {
    /* ignore */
  }
}
